import { useEffect, useState } from "react";
import Screen from "../Layouts/Screen/Screen";
import { Helmet } from "react-helmet-async";
import axios from "axios";
import Spinner from "../../global-components/src/components/spinner/spiner";
import { useAuth, useMessage } from "../../global-components/src";
import { LIMIT_NAME_ADS, PACOCA_API_URL } from "../../global-components/src/utils/vars";
import "./style.css";
import { errorMessage } from "../../global-components/src/utils/text";
import { useNavigate, useParams } from "react-router-dom";
import { formatCurrency, formatCurrencyToFloat } from "../../global-components/src/utils/numbers";
import ConfirmationModal from "../../components/Modals/ConfirmationModal";
import { FrameCategory } from "../../types";
import { t } from "../../global-components/src/i18nTranslate";

type Frame = {
    id?: number;
    name: string;
    expires_at?: string;
    usage_limit: number;
    is_active: boolean;
    is_paid: boolean;
    price?: number | string;
    image_path?: string;
    category_id?: number;
    key: string;
};

const AddFrame = () => {
    const { token } = useAuth();
    const { id: frameId } = useParams();
    const { setMessage } = useMessage();
    const [image, setImage] = useState<File | null>(null);
    const [loadingData, setLoadingData] = useState(false);
    const [loadingCategoryData, setLoadingCategoryData] = useState(true);
    const [loading, setLoading] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);
    const [errorsInput, setErrorsInput] = useState<any>({});
    const [categories, setCategories] = useState<FrameCategory[]>([]);
    const [show, setShow] = useState(false);
    const navigate = useNavigate();
    const [frame, setFrame] = useState<Frame>({
        name: "",
        usage_limit: 100,
        is_active: false,
        key: "",
        price: "2,00",
        is_paid: true,
    });

    useEffect(() => {
        loadCategories();
    }, []);

    const loadCategories = async () => {
        try {
            setLoadingCategoryData(true);
            const res = await axios.get(`${PACOCA_API_URL}/frame-categories`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setCategories(res.data.categories);
        } catch (err){
            const messageError = errorMessage(err);
            setMessage(messageError, "error");
            console.error(err);
            console.error(messageError);
            
        } finally {
            setLoadingCategoryData(false);
        }
    };

    useEffect(() => {
        if (frameId) loadFrame();
    }, [frameId]);

    const loadFrame = async () => {
        try {
            setLoadingData(true);
            const res = await axios.get(`${PACOCA_API_URL}/frames/${frameId}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            let frame = res.data.frame

            if(frame.price){
                frame = { ...frame, price: formatCurrency(frame.price)}
            }
            // setFrame({ ...frame, price: formatCurrency(e.target.value)})
            setFrame(frame);
        } catch (err){
            const messageError = errorMessage(err);
            setMessage(messageError, "error");
            console.error(err);
            console.error(messageError);
            
        } finally {
            setLoadingData(false);
        }
    };

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        const is_paid = frame.is_paid;

        try {

            let errors: Record<string, string[]> = {};
            if (frame.name.length > LIMIT_NAME_ADS) {
                errors.name = [t("errors.input.maxLength", { field: t("inputs.name"), max: LIMIT_NAME_ADS })];
            }
            if (!frame.name.trim().length) {
                errors.name = [t("errors.input.required", { field: t("inputs.name") })];
            }
            if (!frame.category_id) {
                errors.category_id = [t("errors.input.required", { field: t("generics.value") })];
            }
            if (!image && !frame.id) {
                errors.image = [t("errors.input.required", { field: t("inputs.banner") })];
            }
            if (is_paid && (!frame.price || formatCurrencyToFloat(String(frame.price)) <=0)) {
                errors.price = [t("errors.input.required", { field: t("generics.value") })];
            }
            
            if (Object.keys(errors).length > 0) {
                throw {
                    response: {
                        data: {
                            errors
                        }
                    }
                };
            }

            const form = new FormData();
            form.append("name", frame.name);
            if (image) form.append("image", image);
            
            form.append("is_active", frame.is_active ? "1" : "0");
            form.append("is_paid", is_paid ? "1" : "0");
            form.append("category_id", String(frame.category_id));

            if (frame.key?.trim().length) form.append("key", frame?.key);
            if (is_paid) form.append("price", String(formatCurrencyToFloat(String(frame.price))));
            if (is_paid && frame.expires_at && frame.key?.trim().length && frame.expires_at) form.append("expires_at", frame.expires_at);
            if (is_paid && frame.key?.trim().length && frame.usage_limit) form.append("usage_limit", String(frame.usage_limit));
            
            if (frameId) {
                await axios.post(`${PACOCA_API_URL}/frames/${frameId}`, form,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setMessage("Moldura atualizada", "success");
                navigate("/molduras");
            } else {
                await axios.post(`${PACOCA_API_URL}/frames`, form, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setMessage("Moldura criada", "success");
                navigate("/molduras");
            }
        } catch (err: any){
            const messageError = errorMessage(err);
            console.error("Erro criar anuncio: ", messageError);
            console.error("Erro criar anuncio: ", err);
            if (err?.response?.data.errors) {
                setErrorsInput(err.response.data.errors); // Atualiza o estado com os erros
                // setMessage("Revise os campos", "error")
            } else{
                setMessage(messageError, "error")
            }
        } finally {
            setLoading(false);
        }
    };

    const remover = async () => {
        setLoadingDelete(true);

        try {
            await axios.delete(`${PACOCA_API_URL}/frames/${frame?.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );
            setMessage("Moldura apagada", "success");
            navigate("/molduras")
            // setFrames((prevFrames) => prevFrames.filter(f => f.id !== frame?.id));
            setShow(false);
        } catch (err){
            const messageError = errorMessage(err);
            setMessage(messageError, "error");
        } finally {
            setLoadingDelete(false);
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFrame({ ...frame, price: formatCurrency(e.target.value)})
    };
    
    return (
        <Screen title={frameId ? "Editar Moldura" : "Criar Moldura"} paddingTopTitle>
            <Helmet>
                <title>Paçoca - Admin Molduras</title>
            </Helmet>

            <ConfirmationModal
                title="Apagar Moldura"
                text={`Texm certeza que deseja apagar essa moldura? Não é possível recuperar`}
                setPassword={() => {}}
                show={show}
                coverMobile={false}
                onClose={() => setShow(false)}
                onConfirm={remover}
                isLoading={loadingDelete}
            />

            <div className="padding-big-screen">
                {loadingData ? (
                    <>
                        <div className="">
                            {[...Array(10)].map((_, index) => (
                                <div key={index} style={{ height: 40, width: "100%" }} className="mt-2 placeholder"></div>
                            ))}
                        </div>
                    </>
                ): (
                    <form onSubmit={submit} className="frame-form">
                        <label>Nome</label>
                        <input className={`form-control ${errorsInput.name ? 'is-invalid' : ''}`} value={frame.name} onChange={e => setFrame({ ...frame, name: e.target.value })}/>
                        {errorsInput.name && (
                            <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.name[0]}</div>
                        )}

                        <label className="mt-3">Imagem da moldura</label>
                        <input type="file" className={`form-control ${errorsInput.image ? 'is-invalid' : ''}`} onChange={e => setImage(e.target.files?.[0] || null)} />
                        {errorsInput.image && (
                            <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.image[0]}</div>
                        )}

                        <label className="mt-3">Categoria</label>
                        <select 
                            value={frame.category_id} 
                            className="form-select" 
                            onChange={e => setFrame({ ...frame, category_id: Number(e.target.value) })}>
                                <option value="">Selecione a categoria</option>
                               {categories.map(category => (
                                    <option value={category.id}>
                                        {t(`frames.categories.${category.name}`)}
                                    </option>
                                ))}
                        </select>
                        {errorsInput.category_id && (
                            <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.category_id[0]}</div>
                        )}

                        <div className="form-check mt-3">
                            <input type="checkbox" className="form-check-input" checked={frame.is_paid} onChange={e => setFrame({ ...frame, is_paid: e.target.checked })} />
                            <label>Moldura paga</label>
                            <div style={{marginTop: "5px"}} id="passwordHelp" className="form-text">Se for pago precisa de chave de ativação. Se for grátis qualquer pessoa pode adiquirir</div>
                        </div>

                        {!!frame.is_paid && (
                            <>
                                <label className="mt-3">Preço</label>
                                <input type="text" className={`form-control ${errorsInput.price ? 'is-invalid' : ''}`} onChange={handleChange} value={frame.price || ""} />
                                {errorsInput.price && (
                                    <div className="invalid-feedback" style={{ display: "block" }}>{errorsInput.price[0]}</div>
                                )}

                                <label className="mt-3">Key de ativação</label>
                                <input className="form-control" autoCapitalize="none" value={frame.key} onChange={e => setFrame({ ...frame, key: e.target.value })}/>
                                <div style={{marginTop: "5px"}} id="passwordHelp" className="form-text">Chave de ativação para adiquirir a moldura de graça</div>

                                {frame.key?.trim().length ? (
                                    <>
                                        <label className="mt-3">Data de expiração</label>
                                        <input type="datetime-local" className="form-control" value={frame.expires_at || ""} onChange={e => setFrame({ ...frame, expires_at: e.target.value })} />
                                        
                                        <label className="mt-3">Limite de uso</label>
                                        <input type="number" className="form-control" value={frame.usage_limit} onChange={e => setFrame({ ...frame, usage_limit: Number(e.target.value) })}/>
                                    </>
                                ) : null}
                            </>
                        )}

                        <div className="form-check mt-3">
                            <input id="is_active" type="checkbox" className="form-check-input" checked={frame.is_active} onChange={e => setFrame({ ...frame, is_active: e.target.checked })}/>
                            <label htmlFor="is_active">Moldura ativa</label>
                            <div style={{marginTop: "5px"}} id="passwordHelp" className="form-text">Se não for ativa apenas moderadores podem ver e adiquirir a moldura</div>
                        </div>


                        <div className="d-flex justify-content-end">
                            {frameId ? (
                                <div className="col">
                                    <button type="button" onClick={() => setShow(true)} disabled={loadingDelete} className="btn btn-danger mt-4 w-100" style={{ marginRight: 5 }}>
                                        {loadingDelete ? <Spinner size="20px" /> : "Apagar"}
                                    </button>
                                </div>
                            ) : null}

                           <div className="col">
                                <button disabled={loading} className="btn btn-blue mt-4 w-100 ml-2" style={{ marginLeft: 5 }}>
                                    {loading ? <Spinner size="20px" /> : "Salvar"}
                                </button>
                           </div>
                        </div>
                    </form>
                )}
            </div>
        </Screen>
    );
};

export default AddFrame;
